import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import * as Sentry from '@sentry/react';
import { 
  FolderOpen,
  Plus,
  Search,
  Filter,
  Users,
  Calendar,
  FileImage,
  FileVideo,
  Eye,
  Edit3,
  Trash2,
  Share2,
  Download,
  GitBranch,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  User,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Copy,
  RotateCcw,
  Target,
  TrendingUp,
  Zap,
  Award,
  Star,
  BarChart3,
  X,
  Play,
  Settings,
  Upload,
  RefreshCw,
  Loader2,
  Archive,
  Crop as CropIcon,
  Image as ImageIcon
} from 'lucide-react';
import { ProjectService, ProjectWithMembers, CreateProjectRequest } from '../lib/projects';
import { ProjectMemberService } from '../lib/project-members';
import { StorageService } from '../lib/storage';
import { ProjectSettings } from '../components/ProjectSettings';
import GTMService from '../lib/gtm';
import { useCoreAuth } from '../contexts/CoreAuthContext';
import { projectsCache, generateProjectsCacheKey } from '../lib/cache';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Helper function to create aspect ratio crop
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

// Helper function to get cropped image blob
function getCroppedImg(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const { session } = useCoreAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [projects, setProjects] = useState<ProjectWithMembers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'completed' | 'paused' | 'approved' | 'warning' | 'rejected' | 'archived'>('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState<'all' | 'my'>('all');
  const [creating, setCreating] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'draft' | 'paused',
    coverImage: null as File | null,
    deadline: ''
  });
  
  // Crop-related state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [processing, setProcessing] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Project settings state
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithMembers | null>(null);

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, [statusFilter, projectTypeFilter]);

  // Disable body scroll when modals are open
  useEffect(() => {
    const isModalOpen = showNewProjectModal || showCropModal || showProjectSettings;
    
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showNewProjectModal, showCropModal, showProjectSettings]);

  const loadProjects = async (forceRefresh: boolean = false) => {
    if (!session?.user?.id) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Generate cache key based on user and filters
      const cacheKey = generateProjectsCacheKey(
        session.user.id,
        statusFilter,
        projectTypeFilter
      );

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedProjects = projectsCache.get<ProjectWithMembers[]>(cacheKey);
        if (cachedProjects) {
          setProjects(cachedProjects);
          setLoading(false);
          return;
        }
      }
      
      let projectsData: ProjectWithMembers[];
      
      if (projectTypeFilter === 'my') {
        // Load projects where user is assigned as a team member + owned projects
        projectsData = await ProjectMemberService.getAllMyProjects();
      } else {
        // Load all owned projects (default behavior)
        const statusValue = statusFilter === 'all' ? undefined : 
                           statusFilter === 'completed' ? 'active' : statusFilter;
        projectsData = await ProjectService.getUserProjects(statusValue);
      }
      
      // Apply status filter to My Projects if needed
      if (projectTypeFilter === 'my' && statusFilter !== 'all') {
        const statusValue = statusFilter === 'completed' ? 'active' : statusFilter;
        projectsData = projectsData.filter(project => project.status === statusValue);
      }
      
      // Cache the results for 2 minutes
      projectsCache.set(cacheKey, projectsData, 120000);
      
      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to load projects:', err);
      Sentry.captureException(err, {
        tags: {
          operation: 'loadProjects',
          component: 'ProjectsPage',
        },
        extra: {
          statusFilter,
          projectTypeFilter,
        },
      });
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Image and crop handlers
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);

    // Create image URL for cropping
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setCropImageSrc(reader.result?.toString() || '');
      setShowCropModal(true);
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9)); // 16:9 aspect ratio for cover images
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      setError('Please select a crop area');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Create file from blob
      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      });

      setNewProjectData(prev => ({ ...prev, coverImage: croppedFile }));

      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      setPreviewImageUrl(previewUrl);

      // Close crop modal
      setShowCropModal(false);
      setCropImageSrc('');
    } catch (err) {
      console.error('Failed to crop image:', err);
      Sentry.captureException(err, {
        tags: {
          operation: 'cropImage',
          component: 'ProjectsPage',
        },
      });
      setError('Failed to process cropped image');
    } finally {
      setProcessing(false);
    }
  }, [completedCrop]);

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setNewProjectData(prev => ({ ...prev, coverImage: null }));
    setPreviewImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectData.name.trim()) {
      return;
    }

    try {
      setCreating(true);
      
      let coverImageUrl: string | undefined;
      
      // Upload cover image if provided
      if (newProjectData.coverImage) {
        try {
          setUploadingCover(true);
          const uploadResult = await StorageService.uploadCreative(
            newProjectData.coverImage,
            undefined, // No project ID yet
            (progress) => {
            }
          );
          coverImageUrl = uploadResult.url;
        } catch (uploadErr) {
          console.error('Failed to upload cover image:', uploadErr);
          Sentry.captureException(uploadErr, {
            tags: {
              operation: 'uploadCoverImage',
              component: 'ProjectsPage',
            },
          });
          throw new Error('Failed to upload cover image');
        } finally {
          setUploadingCover(false);
        }
      }
      
      const createRequest: CreateProjectRequest = {
        name: newProjectData.name.trim(),
        description: newProjectData.description.trim() || undefined,
        status: newProjectData.status,
        cover_image: coverImageUrl,
        deadline: newProjectData.deadline || undefined
      };

      const newProject = await ProjectService.createProject(createRequest);
      
      // Track project creation
      GTMService.trackCustomEvent('create_project', {
        project_name: newProject.name,
        project_id: newProject.id,
        project_status: newProject.status,
        has_cover_image: !!newProject.cover_image,
        has_deadline: !!newProject.deadline,
        has_description: !!newProject.description
      }, session?.user?.id);
      
      // Clear cache to ensure fresh data on next load
      if (session?.user?.id) {
        const cacheKey = generateProjectsCacheKey(
          session.user.id,
          statusFilter,
          projectTypeFilter
        );
        projectsCache.delete(cacheKey);
      }

      // Add to local state to avoid refetch
      setProjects(prev => [newProject, ...prev]);
      
      // Reset form and close modal
      setNewProjectData({ name: '', description: '', status: 'active', coverImage: null, deadline: '' });
      setPreviewImageUrl(null);
      setShowCropModal(false);
      setCropImageSrc('');
      setShowNewProjectModal(false);
      
      // Navigate to the new project
      navigate(`/dashboard/projects/${newProject.id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
      Sentry.captureException(err, {
        tags: {
          operation: 'createProject',
          component: 'ProjectsPage',
        },
        extra: {
          projectName: newProject.name,
          hasDescription: !!newProject.description,
          hasCoverImage: !!coverImageUrl,
        },
      });
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
      setUploadingCover(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'draft': return 'text-purple-600 bg-purple-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-emerald-600 bg-emerald-100';
      case 'warning': return 'text-amber-600 bg-amber-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'archived': return 'text-gray-600 bg-gray-100';
      case 'deleted': return 'text-slate-600 bg-slate-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDeadlineStatus = (updatedAt: string | null) => {
    if (!updatedAt) return { color: 'text-gray-600', text: 'No update date' };
    
    const lastUpdate = new Date(updatedAt);
    const now = new Date();
    const daysSinceUpdate = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysSinceUpdate === 0) return { color: 'text-green-600', text: 'Updated today' };
    if (daysSinceUpdate === 1) return { color: 'text-green-600', text: 'Updated yesterday' };
    if (daysSinceUpdate <= 7) return { color: 'text-blue-600', text: `Updated ${daysSinceUpdate} days ago` };
    if (daysSinceUpdate <= 30) return { color: 'text-yellow-600', text: `Updated ${daysSinceUpdate} days ago` };
    return { color: 'text-red-600', text: `Updated ${daysSinceUpdate} days ago` };
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Track search events
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    
    // Only track if search term is substantial and user has session
    if (value.trim().length >= 3 && session?.user?.id) {
      const filteredCount = projects.filter(project =>
        project.name.toLowerCase().includes(value.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(value.toLowerCase()))
      ).length;

      GTMService.trackSearch(value.trim(), filteredCount, session.user.id);
    }
  };

  const handleProjectOpen = (projectId: string) => {
    navigate(`/dashboard/projects/${projectId}`);
  };

  const handleProjectSettings = (project: ProjectWithMembers) => {
    setSelectedProject(project);
    setShowProjectSettings(true);
  };

  const handleProjectUpdated = (updatedProject: ProjectWithMembers) => {
    // Clear cache to ensure fresh data on next load
    if (session?.user?.id) {
      const cacheKey = generateProjectsCacheKey(
        session.user.id,
        statusFilter,
        projectTypeFilter
      );
      projectsCache.delete(cacheKey);
    }

    setProjects(prev => 
      prev.map(p => p.id === updatedProject.id ? updatedProject : p)
    );
    setSelectedProject(null);
  };

  const getDefaultCoverImage = () => 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <SEOHead {...getPageSEO('/projects')} />
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Projects</h1>
              <p className="text-sm sm:text-base text-gray-600">
                Organize and manage your creative campaigns with version control.
              </p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={() => loadProjects(true)}
                disabled={loading}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 min-h-[44px]"
              >
                <Plus className="w-5 h-5" />
                <span>New Project</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Search and Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full min-h-[44px]"
            />
          </div>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[44px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="paused">Paused</option>
            <option value="approved">Approved</option>
            <option value="warning">Warning</option>
            <option value="rejected">Rejected</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        
        {/* Project Type Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <span className="text-sm font-medium text-gray-700">Show:</span>
          <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setProjectTypeFilter('all')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all text-center ${
                projectTypeFilter === 'all'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Projects
            </button>
            <button
              onClick={() => setProjectTypeFilter('my')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center sm:justify-start space-x-2 ${
                projectTypeFilter === 'my'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>My Projects</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3 text-gray-600">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-lg">Loading projects...</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredProjects.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 p-12 text-center"
        >
          <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'all' ? 'No matching projects' : 'No projects yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.' 
              : 'Create your first project to start managing creative campaigns.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <button
              onClick={() => setShowNewProjectModal(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 mx-auto"
            >
              <Plus className="w-5 h-5" />
              <span>Create Project</span>
            </button>
          )}
        </motion.div>
      )}

      {/* Project Cards */}
      {!loading && filteredProjects.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg sm:hover:shadow-2xl hover:scale-[1.01] sm:hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
              onClick={() => handleProjectOpen(project.id)}
            >
              {/* Header with Cover Image */}
              <div className="relative h-48 sm:h-56 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                <img
                  src={project.cover_image || getDefaultCoverImage()}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getDefaultCoverImage();
                  }}
                />
                
                {/* Status Badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm ${getStatusColor(project.status || 'active')} shadow-lg`}>
                    {project.status}
                  </span>
                </div>
                
                {/* Member Count */}
                <div className="absolute top-4 left-4 flex items-center space-x-2">
                  <div className="bg-black/60 backdrop-blur-sm border-2 border-white/80 rounded-lg px-2 py-1 flex items-center space-x-1">
                    <Users className="w-3 h-3 text-white" />
                    <span className="text-white text-xs font-medium">{project.memberCount}</span>
                  </div>
                </div>
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Project Title Overlay */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="font-bold text-xl mb-1 drop-shadow-lg">{project.name}</h3>
                  <p className="text-sm text-gray-200 line-clamp-2 drop-shadow-sm">
                    {project.description || 'No description provided'}
                  </p>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* Owner Info */}
                {project.owner && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Project Owner</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {project.owner.name || project.owner.email}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {project.owner.email}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project Stats */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 py-3 border-t border-gray-100">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <FileImage className="w-4 h-4" />
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {project.recentActivity?.creativesCount || 0}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">Creatives</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <BarChart3 className="w-4 h-4" />
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {project.recentActivity?.analysesCount || 0}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">Analyses</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span className="text-base sm:text-lg font-bold text-gray-900">{project.memberCount}</span>
                    </div>
                    <div className="text-xs text-gray-600">Members</div>
                  </div>
                </div>

                {/* Last Updated */}
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Last Updated</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'Unknown'}
                    </div>
                    <div className={`text-xs font-medium ${getDeadlineStatus(project.updated_at).color}`}>
                      {getDeadlineStatus(project.updated_at).text}
                    </div>
                  </div>
                </div>

                {/* Deadline */}
                {project.deadline && (
                  <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">Deadline</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-blue-900">
                        {new Date(project.deadline).toLocaleDateString()}
                      </div>
                      <div className={`text-xs font-medium ${
                        new Date(project.deadline) < new Date() ? 'text-red-600' :
                        new Date(project.deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {new Date(project.deadline) < new Date() ? 'Overdue' :
                         new Date(project.deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'Due soon' :
                         'On track'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 sm:space-x-3 pt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProjectOpen(project.id);
                    }}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-4 py-2.5 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 font-medium text-sm min-h-[44px]"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Open</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/analysis?projectId=${project.id}`);
                    }}
                    className="px-2.5 sm:px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Add Creative"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProjectSettings(project);
                    }}
                    className="px-2.5 sm:px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Project Settings"
                  >
                    <Settings className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* New Project Modal */}
      <AnimatePresence>
        {showNewProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl w-full max-w-md mx-4 max-h-[95vh] sm:max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Create New Project</h3>
                <button
                  onClick={() => {
                    setShowNewProjectModal(false);
                    setNewProjectData({ name: '', description: '', status: 'active', coverImage: null, deadline: '' });
                    setPreviewImageUrl(null);
                    setShowCropModal(false);
                    setCropImageSrc('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={creating || uploadingCover || processing}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Spring Campaign 2024"
                    value={newProjectData.name}
                    onChange={(e) => setNewProjectData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                    disabled={creating || uploadingCover}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Brief description of the project..."
                    value={newProjectData.description}
                    onChange={(e) => setNewProjectData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[80px]"
                    disabled={creating || uploadingCover}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Project Cover Image
                  </label>
                  
                  <div className="space-y-4">
                    {/* Image Preview */}
                    <div className="relative">
                      <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
                        {previewImageUrl ? (
                          <img
                            src={previewImageUrl}
                            alt="Project cover preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-600">No cover image</p>
                              <p className="text-xs text-gray-500 mt-1">16:9 ratio recommended</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Remove Image Button */}
                      {previewImageUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          disabled={creating || uploadingCover || processing}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors disabled:opacity-50"
                          title="Remove image"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Upload Controls */}
                    <div className="flex items-center space-x-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        disabled={creating || uploadingCover || processing}
                      />
                      
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={creating || uploadingCover || processing}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <Upload className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">
                          {previewImageUrl ? 'Change Image' : 'Upload Image'}
                        </span>
                      </button>
                      
                      {processing && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </div>
                      )}
                    </div>

                    {/* Image Info */}
                    {newProjectData.coverImage && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Image ready: {newProjectData.coverImage.name}</span>
                        <span className="text-xs text-gray-500">
                          ({Math.round(newProjectData.coverImage.size / 1024)} KB)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deadline
                  </label>
                  <input
                    type="date"
                    value={newProjectData.deadline}
                    onChange={(e) => setNewProjectData(prev => ({ ...prev, deadline: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={creating || uploadingCover}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Status
                  </label>
                  <select
                    value={newProjectData.status}
                    onChange={(e) => setNewProjectData(prev => ({ ...prev, status: e.target.value as 'active' | 'draft' | 'paused' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={creating || uploadingCover}
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex space-x-3 p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowNewProjectModal(false);
                    setNewProjectData({ name: '', description: '', status: 'active', coverImage: null, deadline: '' });
                    setPreviewImageUrl(null);
                    setShowCropModal(false);
                    setCropImageSrc('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                  disabled={creating || uploadingCover || processing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  disabled={creating || uploadingCover || processing || !newProjectData.name.trim()}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px]"
                >
                  {creating || uploadingCover || processing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>
                        {processing ? 'Processing Image...' : 
                         uploadingCover ? 'Uploading Cover...' : 
                         'Creating Project...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Create Project</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Project Settings Modal */}
      {selectedProject && (
        <ProjectSettings
          project={selectedProject}
          isOpen={showProjectSettings}
          onClose={() => {
            setShowProjectSettings(false);
            setSelectedProject(null);
          }}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {/* Crop Modal */}
      <AnimatePresence>
        {showCropModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-4xl mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <CropIcon className="w-5 h-5" />
                  <span>Crop Cover Image</span>
                </h3>
                <button
                  onClick={handleCropCancel}
                  disabled={processing}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900">Crop Instructions</span>
                  </div>
                  <ul className="text-blue-800 space-y-1 text-xs">
                    <li>• Drag the corners to adjust the crop area</li>
                    <li>• Use a 16:9 aspect ratio for best results</li>
                    <li>• Minimum size: 200×112 pixels</li>
                    <li>• The cropped image will be optimized for web use</li>
                  </ul>
                </div>

                {cropImageSrc && (
                  <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-4">
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={16 / 9}
                      minWidth={200}
                      minHeight={112}
                      className="max-h-[40vh] sm:max-h-[50vh]"
                    >
                      <img
                        ref={imgRef}
                        alt="Crop preview"
                        src={cropImageSrc}
                        style={{ transform: 'scale(1) rotate(0deg)' }}
                        onLoad={onImageLoad}
                        className="max-h-[40vh] sm:max-h-[50vh] object-contain"
                      />
                    </ReactCrop>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCropCancel}
                    disabled={processing}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropComplete}
                    disabled={processing || !completedCrop}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-h-[44px]"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Apply Crop</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}